import { api } from 'aws-blocks';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import BkButton from '../../Components/ui/BkButton';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import SocialLinkCard from '../../Components/SocialLinkCard';
import { COMMUNITY_SOCIALS } from '../../config/community';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import useApiAction from '../../hooks/use-api-action';
import usePageMeta from '../../hooks/use-page-meta';

const fieldClass =
  'w-full border border-bk-border bg-bk-bg px-3 py-2 text-bk-text focus:border-bk-gold focus:outline-none focus:ring-1 focus:ring-bk-gold';

export default function Community() {
  usePageMeta({
    title: 'Community',
    description:
      'Join Blind Kings on Facebook, Discord, and Reddit — or send feedback to help improve the game.',
    path: '/community',
  });

  const feedbackInputRef = useRef<HTMLTextAreaElement>(null);
  const feedbackTypeRef = useRef<HTMLSelectElement>(null);
  const [sent, setSent] = useState(false);
  const { addSnackBar } = useSnackBarContext();
  const {
    userData: { playerId } = {},
    currentUser,
    openLoginModal,
  } = useAuthContext();

  const [submitFeedback, loading] = useApiAction({
    action: async (payload: { feedback?: string; feedbackType?: string }) =>
      api.submitFeedback(String(payload.feedback), String(payload.feedbackType)),
  });

  async function handleFeedbackSubmit(event: FormEvent) {
    event.preventDefault();
    setSent(false);

    if (!currentUser || !playerId) {
      openLoginModal();
      addSnackBar({
        title: 'Log in required',
        message: 'Sign in to submit feedback.',
        variant: 'info',
        timeout: 3000,
      });
      return;
    }

    const feedbackType = feedbackTypeRef.current?.value;
    const feedback = feedbackInputRef.current?.value?.trim();
    if (!feedbackType || !feedback) {
      addSnackBar({
        title: 'Missing details',
        message: !feedback
          ? 'Write a short message before submitting.'
          : 'Choose a feedback type.',
        variant: 'danger',
        timeout: 3000,
      });
      return;
    }

    try {
      await submitFeedback({ feedback, feedbackType });
      if (feedbackInputRef.current) feedbackInputRef.current.value = '';
      setSent(true);
      addSnackBar({
        title: 'Feedback Submitted',
        message: 'Thanks for helping improve Blind Kings.',
        variant: 'success',
        timeout: 3000,
      });
    } catch {
      // snackbar from hook
    }
  }

  return (
    <BkPage>
      <div className="mb-8 space-y-2">
        <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold md:text-3xl">
          Community
        </h1>
        <p className="max-w-2xl text-sm text-bk-muted md:text-base">
          Find players, follow updates, and tell us how Blind Kings can improve.
        </p>
      </div>

      <section className="mb-10 space-y-4" aria-labelledby="community-socials">
        <h2
          id="community-socials"
          className="text-lg font-bold uppercase tracking-wider text-bk-text"
        >
          Where the realm gathers
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {COMMUNITY_SOCIALS.map((link) => (
            <SocialLinkCard key={link.id} link={link} />
          ))}
        </div>
      </section>

      <section className="space-y-4" aria-labelledby="community-feedback">
        <h2
          id="community-feedback"
          className="text-lg font-bold uppercase tracking-wider text-bk-text"
        >
          Feedback
        </h2>
        <BkPanel>
          {!currentUser ? (
            <div className="space-y-4">
              <p className="text-sm text-bk-muted">
                Sign in to send bug reports, feature ideas, or general feedback.
                Your message goes to the Blind Kings team.
              </p>
              <BkButton variant="primary" onClick={openLoginModal}>
                Log in to send feedback
              </BkButton>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={(e) => void handleFeedbackSubmit(e)}>
              <p className="text-sm text-bk-muted">
                Signed in as{' '}
                <span className="text-bk-text">
                  {currentUser.attributes?.email ||
                    currentUser.username ||
                    'player'}
                </span>
                . We read every submission.
              </p>
              <label className="block space-y-1">
                <span className="text-xs uppercase tracking-wide text-bk-muted">
                  Type
                </span>
                <select
                  ref={feedbackTypeRef}
                  className={fieldClass}
                  defaultValue="feedback"
                  name="feedbackType"
                >
                  <option value="feedback">General</option>
                  <option value="bug">Bug</option>
                  <option value="feature">Feature idea</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-xs uppercase tracking-wide text-bk-muted">
                  Message
                </span>
                <textarea
                  ref={feedbackInputRef}
                  name="feedback"
                  className={`${fieldClass} min-h-32`}
                  placeholder="What should we know?"
                  required
                  maxLength={2000}
                />
              </label>
              {sent ? (
                <p className="text-sm font-medium text-bk-gold" role="status">
                  Thanks — your feedback was sent.
                </p>
              ) : null}
              <BkButton type="submit" variant="primary" disabled={loading}>
                {loading ? 'Sending…' : 'Submit feedback'}
              </BkButton>
            </form>
          )}
        </BkPanel>
      </section>

      <p className="mt-10 text-sm text-bk-muted">
        <Link
          className="text-bk-gold underline-offset-2 hover:underline"
          to="/terms"
        >
          Terms of Service
        </Link>
        {' · '}
        <Link
          className="text-bk-gold underline-offset-2 hover:underline"
          to="/privacy"
        >
          Privacy Policy
        </Link>
      </p>
    </BkPage>
  );
}
